import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FileCheck, X } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { gbp, catOf, STATUS_LABELS, setPageMeta } from '../lib/shop';

const ROWS = [
  ['strength', 'Strength'], ['format', 'Format'], ['category', 'Category'],
  ['price', 'Price'], ['coa_available', 'COA'], ['batch_number', 'Batch'],
  ['status', 'Status'], ['specification', 'Specification'],
];

export default function Compare() {
  const { compare, toggleCompare, clearCompare } = useCart();
  useEffect(() => { setPageMeta('Compare Products', 'Side-by-side comparison of VQ Health research compounds.'); }, []);

  const cell = (p, key) => {
    switch (key) {
      case 'category': return <span style={{ color: catOf(p.category).color }}>{catOf(p.category).name}</span>;
      case 'price': return <span className="font-mono2 font-semibold">{p.has_variants ? 'From ' : ''}{gbp(p.price)}</span>;
      case 'coa_available': return p.coa_available
        ? <span className="flex items-center gap-1"><FileCheck className="w-4 h-4" /> Available</span>
        : <span className="text-[#8A8F95]">None published</span>;
      case 'batch_number': return p.batch_number || <span className="text-[#8A8F95]">—</span>;
      case 'status': return STATUS_LABELS[p.status] || p.status;
      case 'specification': return <span className="text-[13px] text-[#575B60] line-clamp-4">{p.specification || '—'}</span>;
      default: return p[key] || '—';
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="compare-page">
      <p className="font-mono2 text-[11px] uppercase tracking-[0.25em] text-[#8A8F95]">Comparison Matrix</p>
      <h1 className="font-display font-black uppercase text-4xl sm:text-5xl tracking-tight mt-2 mb-8">Compare Products</h1>

      {compare.length < 2 ? (
        <div className="border hairline bg-white p-10 text-center" data-testid="compare-empty">
          <p className="font-display font-bold uppercase text-2xl">Select 2–4 products to compare</p>
          <p className="text-sm text-[#575B60] mt-2">Use the compare icon on any product card to build a side-by-side matrix. No recommendations are made — specification only.</p>
          <Link to="/products" className="inline-block mt-6 bg-[#0B0F12] text-white px-6 py-3 font-mono2 text-xs uppercase tracking-widest hover:bg-[#E54D2E] transition-colors">Browse catalogue</Link>
        </div>
      ) : (
        <div className="overflow-x-auto border hairline bg-white">
          <table className="w-full min-w-[640px]" data-testid="compare-table">
            <thead>
              <tr className="border-b hairline">
                <th className="p-4 text-left font-mono2 text-[10px] uppercase tracking-widest text-[#8A8F95] w-32 align-bottom">
                  <button data-testid="compare-clear-all" onClick={clearCompare} className="underline underline-offset-4 hover:text-[#0B0F12]">Clear all</button>
                </th>
                {compare.map((p) => (
                  <th key={p.id} className="p-4 text-left align-top">
                    <div className="relative w-28">
                      <img src={p.image} alt="" className="w-28 h-28 object-cover bg-[#F4F4F0]" />
                      <button data-testid={`compare-remove-${p.id}`} onClick={() => toggleCompare(p)}
                        className="absolute -top-2 -right-2 bg-[#0B0F12] text-white p-1"><X className="w-3 h-3" /></button>
                    </div>
                    <Link to={`/product/${p.slug}`} className="block font-display font-black uppercase text-lg leading-tight mt-3 hover:underline">
                      {p.name}
                    </Link>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {ROWS.map(([key, label]) => (
                <tr key={key} className="border-b hairline last:border-0">
                  <td className="p-4 font-mono2 text-[10px] uppercase tracking-widest text-[#8A8F95] align-top">{label}</td>
                  {compare.map((p) => <td key={p.id} className="p-4 text-sm align-top">{cell(p, key)}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
